import { useEffect, useMemo, useRef, useState } from 'react';

import { AuditDateControls, AuditDownloadButton } from './AuditTableToolbar';
import FiltersSideMenu from './FiltersSideMenu';
import styles from './styles.module.css';
import {
  AuditColumnSelection,
  AuditDateRange,
  AuditRow,
  turbineSelectionKey
} from './types';
import {
  buildAuditFilters,
  buildAuditLocations,
  buildReportRequest,
  downloadBlob,
  getDefaultAuditDateRange,
  isSameAuditRequest,
  mapReportToGroups
} from './utils';
import {
  AdminCardHeader,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import DataTable, {
  DataTableColumn,
  NestedAccordionLocation
} from '@/components/DataTable';
import { Turbine } from '@/containers/GlobalViewContainer/types';
import {
  AuditReportRequest,
  useDownloadAuditingReport,
  useGetAuditingReport
} from '@/hooks/services/Audit';
import { useGetTurbinesList } from '@/hooks/services/Turbines';

const DEFAULT_COLUMN_SELECTION: AuditColumnSelection = {
  showFuel: true,
  showEngineHours: true,
  showMonthlyStarts: true,
  showMonthlyStops: true
};

const TURBINE_COLUMN: DataTableColumn<AuditRow> = {
  field: 'turbine',
  headerName: 'Turbine',
  width: '120px'
};

const renderCountCell = (value: number | null | undefined) =>
  value === null || value === undefined ? <span>-</span> : <span>{value}</span>;

const buildColumns = (
  selection: AuditColumnSelection
): DataTableColumn<AuditRow>[] => {
  const columns: DataTableColumn<AuditRow>[] = [
    TURBINE_COLUMN,
    {
      field: 'date',
      headerName: 'Date',
      width: '130px',
      renderCell: ({ row }) => (
        <span>
          {row.date} <span className={styles.blueText}>{row.timezone}</span>
        </span>
      )
    }
  ];

  if (selection.showFuel) {
    columns.push({
      field: 'fuelData',
      headerName: 'Fuel Consumption',
      width: '170px',
      renderCell: ({ value }) =>
        value === null || value === undefined ? (
          <span>-</span>
        ) : (
          <span>
            {value} <span className={styles.blueText}>gal</span>
          </span>
        )
    });
  }

  if (selection.showEngineHours) {
    columns.push({
      field: 'engineFiredHours',
      headerName: 'Engine Fired Hours',
      width: '130px',
      renderCell: ({ value }) =>
        value === null || value === undefined ? (
          <span>-</span>
        ) : (
          <span>
            {value} <span className={styles.blueText}>hr</span>
          </span>
        )
    });
  }

  if (selection.showMonthlyStarts) {
    columns.push({
      field: 'monthlyStarts',
      headerName: 'Monthly Starts',
      width: '130px',
      renderCell: ({ value }) => renderCountCell(value)
    });
  }

  if (selection.showMonthlyStops) {
    columns.push({
      field: 'monthlyStops',
      headerName: 'Monthly Stops',
      width: '130px',
      renderCell: ({ value }) => renderCountCell(value)
    });
  }

  return columns;
};

interface AppliedFilters {
  request: AuditReportRequest;
  selection: AuditColumnSelection;
}

export default function AuditViewContainer() {
  const { data: turbinesResponse, isLoading: isTurbinesLoading } =
    useGetTurbinesList({
      useQueryOptions: {
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
      }
    });
  const turbines = ((turbinesResponse as { data?: Turbine[] })?.data ??
    []) as Turbine[];

  const auditLocations = useMemo(
    () => buildAuditLocations(turbines),
    [turbines]
  );

  const [selectedKeys, setSelectedKeys] = useState<Record<string, boolean>>({});
  const [showFuel, setShowFuel] = useState(DEFAULT_COLUMN_SELECTION.showFuel);
  const [showEngineHours, setShowEngineHours] = useState(
    DEFAULT_COLUMN_SELECTION.showEngineHours
  );
  const [showMonthlyStarts, setShowMonthlyStarts] = useState(
    DEFAULT_COLUMN_SELECTION.showMonthlyStarts
  );
  const [showMonthlyStops, setShowMonthlyStops] = useState(
    DEFAULT_COLUMN_SELECTION.showMonthlyStops
  );
  const [search, setSearch] = useState('');
  const [range, setRange] = useState<AuditDateRange>(getDefaultAuditDateRange);
  const [applied, setApplied] = useState<AppliedFilters | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current || isTurbinesLoading) return;
    initializedRef.current = true;

    const initialSelection: Record<string, boolean> = {};
    auditLocations.forEach(location => {
      location.turbines.forEach(turbine => {
        initialSelection[turbineSelectionKey(location.id, turbine.id)] = true;
      });
    });

    const initialRequest = buildReportRequest(
      range,
      auditLocations,
      initialSelection,
      buildAuditFilters(DEFAULT_COLUMN_SELECTION)
    );

    setSelectedKeys(initialSelection);
    if (initialRequest) {
      setApplied({
        request: initialRequest,
        selection: DEFAULT_COLUMN_SELECTION
      });
    }
    setIsInitializing(false);
  }, [auditLocations, isTurbinesLoading, range]);

  const {
    data: reportResponse,
    isFetching: isReportFetching,
    refetch: refetchReport
  } = useGetAuditingReport(applied?.request ?? null);

  const allTurbinesSelected = useMemo(() => {
    const values = Object.values(selectedKeys);
    return values.length > 0 && values.every(Boolean);
  }, [selectedKeys]);

  const columnSelection = useMemo<AuditColumnSelection>(
    () => ({
      showFuel,
      showEngineHours,
      showMonthlyStarts,
      showMonthlyStops
    }),
    [showFuel, showEngineHours, showMonthlyStarts, showMonthlyStops]
  );

  const columns = useMemo(
    () => buildColumns(applied?.selection ?? DEFAULT_COLUMN_SELECTION),
    [applied?.selection]
  );

  const tableGroups = useMemo<NestedAccordionLocation<AuditRow>[]>(
    () => mapReportToGroups(reportResponse?.data?.locations ?? []),
    [reportResponse]
  );

  const toggleTurbine = (locationId: string, turbineId: string) => {
    const key = turbineSelectionKey(locationId, turbineId);
    setSelectedKeys(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleLocation = (locationId: string) => {
    const location = auditLocations.find(item => item.id === locationId);
    if (!location) return;
    const shouldSelect = !location.turbines.every(
      turbine => selectedKeys[turbineSelectionKey(locationId, turbine.id)]
    );
    setSelectedKeys(prev => {
      const next = { ...prev };
      location.turbines.forEach(turbine => {
        next[turbineSelectionKey(locationId, turbine.id)] = shouldSelect;
      });
      return next;
    });
  };

  const addTurbines = (keys: string[]) => {
    if (keys.length === 0) return;
    setSelectedKeys(prev => {
      const next = { ...prev };
      keys.forEach(key => {
        next[key] = true;
      });
      return next;
    });
  };

  const toggleAllLocations = () => {
    const shouldSelect = !allTurbinesSelected;
    setSelectedKeys(() => {
      const next: Record<string, boolean> = {};
      auditLocations.forEach(location => {
        location.turbines.forEach(turbine => {
          next[turbineSelectionKey(location.id, turbine.id)] = shouldSelect;
        });
      });
      return next;
    });
  };

  const clearColumns = () => {
    setShowFuel(false);
    setShowEngineHours(false);
    setShowMonthlyStarts(false);
    setShowMonthlyStops(false);
  };

  const clearTurbines = () => {
    setSearch('');
    const next: Record<string, boolean> = {};
    auditLocations.forEach(location => {
      location.turbines.forEach(turbine => {
        next[turbineSelectionKey(location.id, turbine.id)] = false;
      });
    });
    setSelectedKeys(next);
  };

  const applyFilters = () => {
    const request = buildReportRequest(
      range,
      auditLocations,
      selectedKeys,
      buildAuditFilters(columnSelection)
    );
    if (!request) return;
    const shouldRefetch = isSameAuditRequest(applied?.request, request);
    setApplied({ request, selection: columnSelection });
    if (shouldRefetch) {
      void refetchReport();
    }
  };

  const downloadReport = useDownloadAuditingReport();

  const handleDownload = async () => {
    const request =
      applied?.request ??
      buildReportRequest(
        range,
        auditLocations,
        selectedKeys,
        buildAuditFilters(columnSelection)
      );
    if (!request) return;
    const blob = await downloadReport.mutateAsync({ request, format: 'csv' });
    downloadBlob('turbinesHistoryAuditingReport.csv', blob);
  };

  if (isTurbinesLoading) {
    return (
      <div className={styles.layout}>
        <ContainerLoader />
      </div>
    );
  }

  return (
    <div className={styles.layout}>
      <FiltersSideMenu
        locations={auditLocations}
        selectedKeys={selectedKeys}
        allTurbinesSelected={allTurbinesSelected}
        showFuel={showFuel}
        showEngineHours={showEngineHours}
        showMonthlyStarts={showMonthlyStarts}
        showMonthlyStops={showMonthlyStops}
        search={search}
        onToggleTurbine={toggleTurbine}
        onToggleLocation={toggleLocation}
        onToggleAllLocations={toggleAllLocations}
        onToggleFuel={setShowFuel}
        onToggleEngineHours={setShowEngineHours}
        onToggleMonthlyStarts={setShowMonthlyStarts}
        onToggleMonthlyStops={setShowMonthlyStops}
        onSearchChange={setSearch}
        onAddTurbines={addTurbines}
        onClearColumns={clearColumns}
        onClearTurbines={clearTurbines}
      />
      <div className={styles.content}>
        <div className={styles.container}>
          <AdminCardHeader
            left={
              <AuditDateControls
                range={range}
                onRangeChange={setRange}
                onApply={applyFilters}
              />
            }
            right={
              <AuditDownloadButton
                onDownload={handleDownload}
                disabled={tableGroups.length === 0}
              />
            }
          />
          <AdminTableBody>
            {isInitializing || isReportFetching ? (
              <ContainerLoader />
            ) : (
              <DataTable<AuditRow>
                variant="nestedAccordion"
                rows={[]}
                columns={columns}
                accordionGroups={tableGroups}
              />
            )}
          </AdminTableBody>
        </div>
      </div>
    </div>
  );
}
